// ABOUTME: API slice for support ticket management endpoints.
// ABOUTME: Handles tickets, messages, attachments, linked items, labels, and dashboard metrics.

import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const BASE = "/support-tickets";
const LABELS_BASE = "/support-ticket-labels";
const ApiName = "SupportTickets";
const ApiSingleName = "SupportTicketSingle";
const ApiMessagesName = "SupportTicketMessages";
const ApiAttachmentsName = "SupportTicketAttachments";
const ApiLinkedItemsName = "SupportTicketLinkedItems";
const ApiLabelsName = "SupportTicketLabels";
const ApiDashboardName = "SupportTicketDashboard";
const ApiLabelsCrudName = "SupportTicketLabelsCrud";
const ApiStaleListName = "SupportTicketStaleList";
const ApiUnassignedListName = "SupportTicketUnassignedList";
const ApiWarehouseRelationStatsName = "SupportTicketWarehouseRelationStats";
const ApiWarehouseRelationHistoryName = "SupportTicketWarehouseRelationHistory";
const ApiWarehouseOpenTicketsName = "SupportTicketWarehouseOpenTickets";

const buildUrl = (endpoint) => BASE + endpoint;
const buildLabelsUrl = (endpoint) => LABELS_BASE + endpoint;

const supportTicketApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        // List tickets with filters
        getSupportTickets: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: ApiName }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getSupportTickets', ApiName)
        }),

        // Get single ticket details
        getSupportTicket: builder.query({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: ApiSingleName, id }],
        }),

        // Create ticket (supports file uploads with FormData)
        createSupportTicket: builder.mutation({
            query: (ticketData) => {
                // If ticketData is FormData, pass it directly
                // Otherwise, wrap it for backward compatibility
                const body = ticketData instanceof FormData ? ticketData : ticketData;

                return {
                    url: buildUrl('/create'),
                    method: 'POST',
                    body: body,
                    // Don't set Content-Type header for FormData - browser sets it with boundary
                };
            },
            invalidatesTags: (result, error, arg) => [
                { type: ApiName },
                { type: ApiDashboardName },
                { type: 'SupportTicketUpdates' },
            ],
        }),

        // Update ticket (status/priority/assignment)
        updateSupportTicket: builder.mutation({
            query: ({ id, data }) => ({
                url: buildUrl(`/${id}`),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: ApiName },
                { type: ApiSingleName, id },
                { type: ApiDashboardName },
                // Status changes (resolve/reopen/close) affect which list a
                // ticket lives in — refresh the for-me / needs-review lists.
                { type: 'SupportTicketUpdates' },
                // Closing a ticket changes the warehouse-side history stats
                // and the per-relation CLOSED list.
                { type: ApiWarehouseRelationStatsName },
                { type: ApiWarehouseRelationHistoryName },
                // Status/assignment changes also affect the open-tickets grid.
                { type: ApiWarehouseOpenTicketsName }
            ],
        }),

        // Add reply or internal note (supports file uploads with FormData)
        addSupportTicketMessage: builder.mutation({
            query: ({ id, data }) => {
                // If data is FormData, pass it directly
                // Otherwise, wrap it as JSON for backward compatibility
                const body = data instanceof FormData ? data : data;

                return {
                    url: buildUrl(`/${id}/messages`),
                    method: 'POST',
                    body: body,
                    // Don't set Content-Type header for FormData - browser sets it with boundary
                };
            },
            invalidatesTags: (result, error, { id }) => [
                { type: ApiSingleName, id },
                { type: ApiMessagesName, id },
                { type: 'SupportTicketUpdates' },
                // A public reply flips the responsibility split (last public
                // sender) and can change the needs-review count.
                { type: ApiDashboardName }
            ],
        }),

        // Upload file attachment
        addSupportTicketAttachment: builder.mutation({
            query: ({ id, file }) => {
                const formData = new FormData();
                formData.append('file', file);

                return {
                    url: buildUrl(`/${id}/attachments`),
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (result, error, { id }) => [
                { type: ApiSingleName, id },
                { type: ApiAttachmentsName, id }
            ],
        }),

        // Link order/product/etc to ticket
        addSupportTicketLinkedItem: builder.mutation({
            query: ({ id, data }) => ({
                url: buildUrl(`/${id}/linked-items`),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: ApiSingleName, id },
                { type: ApiLinkedItemsName, id },
                { type: ApiMessagesName, id },
                { type: 'SupportTicketUpdates' },
            ],
        }),

        // Remove linked item from ticket
        removeSupportTicketLinkedItem: builder.mutation({
            query: ({ id, itemId }) => ({
                url: buildUrl(`/${id}/linked-items/${itemId}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: ApiSingleName, id },
                { type: ApiLinkedItemsName, id },
                { type: ApiMessagesName, id },
                { type: 'SupportTicketUpdates' },
            ],
        }),

        // Add label to ticket
        addSupportTicketLabel: builder.mutation({
            query: ({ id, data }) => ({
                url: buildUrl(`/${id}/labels`),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: ApiSingleName, id },
                { type: ApiLabelsName, id }
            ],
        }),

        // Remove label from ticket
        removeSupportTicketLabel: builder.mutation({
            query: ({ id, labelId }) => ({
                url: buildUrl(`/${id}/labels/${labelId}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: ApiSingleName, id },
                { type: ApiLabelsName, id }
            ],
        }),

        // Get warehouse dashboard metrics
        getSupportTicketDashboard: builder.query({
            query: (body) => ({
                url: buildUrl('/dashboard'),
                method: 'POST',
                body: body,
            }),
            providesTags: (result, error, arg) => [{ type: ApiDashboardName }],
        }),

        // Stale tickets sidebar list. body: { filter: 'unassigned' | 'stuck', relationId? }
        getDashboardStaleList: builder.query({
            query: (body) => ({
                url: buildUrl('/dashboard/stale-list'),
                method: 'POST',
                body: body,
            }),
            providesTags: (result, error, arg) => [
                { type: ApiStaleListName },
                { type: ApiDashboardName },
            ],
        }),

        // Paginated unassigned tickets list (Nieuw binnengekomen tab).
        // body: { relationId? } — body must have ≥1 key for ShDataGrid2 not to skip.
        getDashboardUnassignedList: builder.query({
            query: ({ args }) => ({
                url: buildUrl(`/dashboard/unassigned-list?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: ApiUnassignedListName }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getDashboardUnassignedList', ApiUnassignedListName),
        }),

        // Get relation-portal dashboard (counts + recent tickets, scoped by relation_id header)
        getRelationSupportTicketDashboard: builder.query({
            query: () => ({
                url: buildUrl('/dashboard/relation'),
                method: 'POST',
            }),
            providesTags: (result, error, arg) => [
                { type: ApiDashboardName },
                { type: 'SupportTicketUpdates' },
            ],
        }),

        // Label CRUD operations
        getSupportTicketLabels: builder.query({
            query: () => ({
                url: buildLabelsUrl(''),
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: ApiLabelsCrudName }],
        }),

        createSupportTicketLabelType: builder.mutation({
            query: (labelData) => ({
                url: buildLabelsUrl(''),
                method: 'POST',
                body: labelData,
            }),
            invalidatesTags: (result, error, arg) => [{ type: ApiLabelsCrudName }],
        }),

        updateSupportTicketLabelType: builder.mutation({
            query: ({ id, data }) => ({
                url: buildLabelsUrl(`/${id}`),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, arg) => [{ type: ApiLabelsCrudName }],
        }),

        deleteSupportTicketLabelType: builder.mutation({
            query: (id) => ({
                url: buildLabelsUrl(`/${id}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{ type: ApiLabelsCrudName }],
        }),

        // Get tickets with unread updates (Werkscherm Updates List)
        getSupportTicketsUpdatesForMe: builder.query({
            queryFn: async (_args, { getState }, _extraOptions, baseQuery) => {
                const userId = getState().auth.user?.id;
                return baseQuery({
                    url: buildUrl('/updates-for-me'),
                    method: 'POST',
                    body: {  userId },
                });
            },
            providesTags: (result, error, arg) => [{ type: 'SupportTicketUpdates' }],
        }),
        // Get tickets with unread updates (Werkscherm Updates List)
        getSupportTicketsForMe: builder.query({
            queryFn: async ({ page = 1, limit = 20,sortBy = 'updatedAt', sortOrder = 'DESC' } = {}, { getState }, _extraOptions, baseQuery) => {
                const userId = getState().auth.user?.id;
                return baseQuery({
                    url: buildUrl('/all-for-me'),
                    method: 'POST',
                    body: { page, limit, sortBy, sortOrder, userId },
                });
            },
            providesTags: (result, error, arg) => [{ type: 'SupportTicketUpdates' }],
        }),

        // Get RESOLVED tickets where the customer has replied since resolution.
        // Not paginated — bounded by the 7-day resolve grace window.
        getSupportTicketsNeedsReviewForMe: builder.query({
            queryFn: async ({ sortBy = 'updatedAt', sortOrder = 'DESC' } = {}, _api, _extraOptions, baseQuery) => {
                return baseQuery({
                    url: buildUrl('/needs-review-for-me'),
                    method: 'POST',
                    body: { sortBy, sortOrder },
                });
            },
            providesTags: (result, error, arg) => [{ type: 'SupportTicketUpdates' }],
        }),

        // CLOSED ticket history for the relation portal. Paginated for
        // infinite scroll, with optional server-side search across
        // ticketNumber + title.
        getRelationClosedTickets: builder.query({
            query: (arg) => {
                const a = arg || {};
                return {
                    url: buildUrl('/relation/closed'),
                    method: 'POST',
                    body: {
                        page: a.page ?? 1,
                        limit: a.limit ?? 20,
                        search: a.search ?? '',
                        sortOrder: a.sortOrder ?? 'DESC',
                    },
                };
            },
            providesTags: (result, error, arg) => [{ type: 'SupportTicketUpdates' }],
        }),

        // Warehouse-side open tickets grid. ShDataGrid2-shaped:
        // args = { page, size, body: { searchString, sort, relationId?, mineOnly? } }
        getWarehouseOpenTickets: builder.query({
            query: ({ args }) => ({
                url: buildUrl(`/warehouse/open-tickets?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: ApiWarehouseOpenTicketsName }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getWarehouseOpenTickets', ApiWarehouseOpenTicketsName),
        }),

        // Warehouse-side history: list of relations with aggregated ticket
        // stats (totals, open, last resolved, last activity). No pagination —
        // bounded by relation count. Server-side sort + name search.
        getWarehouseRelationsWithTicketStats: builder.query({
            query: ({ sortBy = 'totalTickets', sortOrder = 'DESC', search = '' } = {}) => ({
                url: buildUrl('/warehouse/relations-with-tickets'),
                method: 'POST',
                body: { sortBy, sortOrder, search },
            }),
            providesTags: (result, error, arg) => [{ type: ApiWarehouseRelationStatsName }],
        }),

        // Warehouse-side history: CLOSED tickets for a given relation.
        // ShDataGrid2-shaped: args = { id: relationId, page, size, body }.
        getWarehouseRelationTicketHistory: builder.query({
            query: ({ args }) => ({
                url: buildUrl(`/warehouse/relation/${args.id}/history?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: ApiWarehouseRelationHistoryName }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getWarehouseRelationTicketHistory', ApiWarehouseRelationHistoryName),
        }),

        // Mark ticket as seen
        markSupportTicketAsSeen: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/${id}/mark-seen`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, id) => [
                { type: 'SupportTicketUpdates' },
                { type: ApiSingleName, id }
            ],
        }),

        // Bulk assign tickets
        bulkAssignSupportTickets: builder.mutation({
            query: ({ ticketIds, assignedToId }) => ({
                url: buildUrl('/bulk-assign'),
                method: 'PUT',
                body: { ticketIds, assignedToId },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: ApiName },
                { type: ApiDashboardName },
                { type: ApiStaleListName },
                { type: ApiUnassignedListName },
                { type: 'SupportTicketUpdates' },
                { type: ApiWarehouseOpenTicketsName }
            ],
        }),
    })
});

export const {
    useGetSupportTicketsQuery,
    useGetSupportTicketQuery,
    useCreateSupportTicketMutation,
    useUpdateSupportTicketMutation,
    useAddSupportTicketMessageMutation,
    useAddSupportTicketAttachmentMutation,
    useAddSupportTicketLinkedItemMutation,
    useRemoveSupportTicketLinkedItemMutation,
    useAddSupportTicketLabelMutation,
    useRemoveSupportTicketLabelMutation,
    useGetSupportTicketDashboardQuery,
    useGetDashboardStaleListQuery,
    useGetDashboardUnassignedListQuery,
    useGetRelationSupportTicketDashboardQuery,
    useGetSupportTicketLabelsQuery,
    useCreateSupportTicketLabelTypeMutation,
    useUpdateSupportTicketLabelTypeMutation,
    useDeleteSupportTicketLabelTypeMutation,
    useGetSupportTicketsUpdatesForMeQuery,
    useGetSupportTicketsForMeQuery,
    useGetSupportTicketsNeedsReviewForMeQuery,
    useGetRelationClosedTicketsQuery,
    useGetWarehouseOpenTicketsQuery,
    useGetWarehouseRelationsWithTicketStatsQuery,
    useGetWarehouseRelationTicketHistoryQuery,
    useMarkSupportTicketAsSeenMutation,
    useBulkAssignSupportTicketsMutation,
} = supportTicketApiSlice;
